// RRULE schedules for recurring work (M1-05; 02 §recurring_work, 04 `recurring-work`): a parser
// and an occurrence generator for the RFC 5545 subset the product uses, evaluated as wall-clock
// time in the company's calendar time zone (DST-safe through Luxon: a time inside a
// spring-forward gap moves forward by the gap; a time inside a fall-back fold is its first
// occurrence, the rule Work OS `lib/dates.ts zonedInstant` follows).
//
// Supported: FREQ = DAILY | WEEKLY | MONTHLY | YEARLY; INTERVAL; BYDAY (ordinals such as `1MO`
// or `-1FR` with MONTHLY, and with YEARLY when BYMONTH is given); BYMONTHDAY (negative counts
// from the month's end); BYMONTH; BYHOUR; BYMINUTE; BYSETPOS; WKST; COUNT; UNTIL. Everything
// else (HOURLY and finer, BYWEEKNO, BYYEARDAY, BYSECOND other than 0, an RRULE with a DTSTART
// line) is refused with a reason, never silently ignored.
//
// No DTSTART travels with the rule (the column holds the RRULE alone), so the caller supplies the
// anchor: the start of the series (`recurring_work.created_at`). The anchor sets the period grid
// INTERVAL counts from, the defaults RFC 5545 takes from DTSTART (the weekday of a WEEKLY rule
// without BYDAY, the day of a MONTHLY rule without BYMONTHDAY/BYDAY, the month and day of a plain
// YEARLY rule) and where COUNT starts counting. The time of day defaults to 00:00, not the
// anchor's time, so the moment a schedule was saved never leaks into it. Unlike RFC 5545 the
// anchor itself is an occurrence only when it matches the rule.
import { DateTime } from "luxon";

export type Freq = "DAILY" | "WEEKLY" | "MONTHLY" | "YEARLY";

/** 1 = Monday … 7 = Sunday (Luxon and ISO numbering). */
export type Weekday = 1 | 2 | 3 | 4 | 5 | 6 | 7;

export type ByDay = { weekday: Weekday /** nth in the period; 0 = every one. */; n: number };

export type Rule = {
  freq: Freq;
  interval: number;
  byDay: ByDay[];
  byMonthDay: number[];
  byMonth: number[];
  byHour: number[];
  byMinute: number[];
  bySetPos: number[];
  wkst: Weekday;
  count: number | null;
  /** Inclusive end, as an instant. */
  until: Date | null;
};

export type ParseResult = { ok: true; rule: Rule } | { ok: false; reason: string };

const DAYS: Record<string, Weekday> = { MO: 1, TU: 2, WE: 3, TH: 4, FR: 5, SA: 6, SU: 7 };
const DAY_NAMES = ["", "MO", "TU", "WE", "TH", "FR", "SA", "SU"] as const;
const FREQS: readonly Freq[] = ["DAILY", "WEEKLY", "MONTHLY", "YEARLY"];
/** Periods scanned before a rule is declared to never occur again (≥ 400 years of months). */
const MAX_PERIODS = 5000;

function ints(raw: string, min: number, max: number, nonZero = false): number[] | null {
  const out: number[] = [];
  for (const part of raw.split(",")) {
    if (!/^[+-]?\d{1,4}$/.test(part)) return null;
    const n = Number(part);
    if (n < min || n > max || (nonZero && n === 0)) return null;
    out.push(n);
  }
  return [...new Set(out)].sort((a, b) => a - b);
}

function parseUntil(raw: string): Date | null {
  const m = /^(\d{4})(\d{2})(\d{2})(?:T(\d{2})(\d{2})(\d{2})Z?)?$/.exec(raw);
  if (!m) return null;
  const [, y, mo, d, hh, mi, ss] = m;
  const t = Date.UTC(
    Number(y),
    Number(mo) - 1,
    Number(d),
    hh ? Number(hh) : 23,
    mi ? Number(mi) : 59,
    ss ? Number(ss) : 59,
  );
  const date = new Date(t);
  return date.getUTCMonth() === Number(mo) - 1 ? date : null;
}

/** Parses `FREQ=…;…` (an optional `RRULE:` prefix); every unsupported part is a reason. */
export function parseRule(text: string): ParseResult {
  const fail = (reason: string): ParseResult => ({ ok: false, reason });
  const raw = text.trim().replace(/^RRULE:/i, "");
  if (raw.length === 0 || raw.length > 500) return fail("empty or longer than 500 characters");
  if (/[\r\n]/.test(raw)) return fail("one RRULE line only (no DTSTART or EXDATE lines)");
  const parts = new Map<string, string>();
  for (const part of raw.split(";")) {
    if (part === "") continue;
    const eq = part.indexOf("=");
    if (eq <= 0) return fail(`"${part.slice(0, 40)}" is not NAME=VALUE`);
    const name = part.slice(0, eq).toUpperCase();
    if (parts.has(name)) return fail(`${name} given twice`);
    parts.set(name, part.slice(eq + 1).toUpperCase());
  }
  const freq = parts.get("FREQ");
  if (!freq) return fail("FREQ is required");
  if (!FREQS.includes(freq as Freq))
    return fail(`FREQ=${freq.slice(0, 20)} is not supported (DAILY, WEEKLY, MONTHLY or YEARLY)`);
  const known = new Set([
    "FREQ",
    "INTERVAL",
    "BYDAY",
    "BYMONTHDAY",
    "BYMONTH",
    "BYHOUR",
    "BYMINUTE",
    "BYSECOND",
    "BYSETPOS",
    "WKST",
    "COUNT",
    "UNTIL",
  ]);
  for (const name of parts.keys())
    if (!known.has(name)) return fail(`${name.slice(0, 20)} is not supported`);
  const rule: Rule = {
    freq: freq as Freq,
    interval: 1,
    byDay: [],
    byMonthDay: [],
    byMonth: [],
    byHour: [0],
    byMinute: [0],
    bySetPos: [],
    wkst: 1,
    count: null,
    until: null,
  };
  const interval = parts.get("INTERVAL");
  if (interval !== undefined) {
    const v = ints(interval, 1, 999);
    if (!v || v.length !== 1) return fail("INTERVAL must be one number from 1 to 999");
    rule.interval = v[0] as number;
  }
  const byDay = parts.get("BYDAY");
  if (byDay !== undefined) {
    for (const item of byDay.split(",")) {
      const m = /^([+-]?\d{1,2})?(MO|TU|WE|TH|FR|SA|SU)$/.exec(item);
      if (!m) return fail(`BYDAY "${item.slice(0, 10)}" is not a weekday`);
      const n = m[1] ? Number(m[1]) : 0;
      if (n !== 0 && (Math.abs(n) > 53 || (rule.freq === "MONTHLY" && Math.abs(n) > 5)))
        return fail(`BYDAY "${item}" has an ordinal out of range`);
      rule.byDay.push({ weekday: DAYS[m[2] as string] as Weekday, n });
    }
  }
  const byMonthDay = parts.get("BYMONTHDAY");
  if (byMonthDay !== undefined) {
    const v = ints(byMonthDay, -31, 31, true);
    if (!v) return fail("BYMONTHDAY must list days from 1 to 31 or -31 to -1");
    rule.byMonthDay = v;
  }
  const byMonth = parts.get("BYMONTH");
  if (byMonth !== undefined) {
    const v = ints(byMonth, 1, 12);
    if (!v) return fail("BYMONTH must list months from 1 to 12");
    rule.byMonth = v;
  }
  const byHour = parts.get("BYHOUR");
  if (byHour !== undefined) {
    const v = ints(byHour, 0, 23);
    if (!v) return fail("BYHOUR must list hours from 0 to 23");
    rule.byHour = v;
  }
  const byMinute = parts.get("BYMINUTE");
  if (byMinute !== undefined) {
    const v = ints(byMinute, 0, 59);
    if (!v) return fail("BYMINUTE must list minutes from 0 to 59");
    rule.byMinute = v;
  }
  const bySecond = parts.get("BYSECOND");
  if (bySecond !== undefined && bySecond !== "0")
    return fail("BYSECOND is not supported (schedules are to the minute)");
  const bySetPos = parts.get("BYSETPOS");
  if (bySetPos !== undefined) {
    const v = ints(bySetPos, -366, 366, true);
    if (!v) return fail("BYSETPOS must list positions from 1 to 366 or -366 to -1");
    rule.bySetPos = v;
  }
  const wkst = parts.get("WKST");
  if (wkst !== undefined) {
    const d = DAYS[wkst];
    if (!d) return fail("WKST must be a weekday (MO … SU)");
    rule.wkst = d;
  }
  const count = parts.get("COUNT");
  const until = parts.get("UNTIL");
  if (count !== undefined && until !== undefined) return fail("COUNT and UNTIL are exclusive");
  if (count !== undefined) {
    const v = ints(count, 1, 10_000);
    if (!v || v.length !== 1) return fail("COUNT must be one number from 1 to 10000");
    rule.count = v[0] as number;
  }
  if (until !== undefined) {
    const d = parseUntil(until);
    if (!d) return fail("UNTIL must be a date (YYYYMMDD) or a UTC time (YYYYMMDDTHHMMSSZ)");
    rule.until = d;
  }
  // RFC 5545 §3.3.10 combinations, narrowed to what is supported.
  if (rule.freq === "WEEKLY" && rule.byMonthDay.length > 0)
    return fail("BYMONTHDAY does not apply to a WEEKLY rule");
  if ((rule.freq === "DAILY" || rule.freq === "WEEKLY") && rule.byDay.some((d) => d.n !== 0))
    return fail("a BYDAY ordinal (such as 1MO) needs FREQ=MONTHLY or YEARLY");
  if (rule.freq === "YEARLY" && rule.byDay.some((d) => d.n !== 0) && rule.byMonth.length === 0)
    return fail("a yearly BYDAY ordinal needs BYMONTH (week-of-year ordinals are not supported)");
  return { ok: true, rule };
}

/** Back to text, in a fixed part order (what the API stores after validation). */
export function formatRule(rule: Rule): string {
  const parts = [`FREQ=${rule.freq}`];
  if (rule.interval !== 1) parts.push(`INTERVAL=${rule.interval}`);
  if (rule.byMonth.length > 0) parts.push(`BYMONTH=${rule.byMonth.join(",")}`);
  if (rule.byMonthDay.length > 0) parts.push(`BYMONTHDAY=${rule.byMonthDay.join(",")}`);
  if (rule.byDay.length > 0)
    parts.push(`BYDAY=${rule.byDay.map((d) => `${d.n || ""}${DAY_NAMES[d.weekday]}`).join(",")}`);
  if (rule.byHour.join() !== "0") parts.push(`BYHOUR=${rule.byHour.join(",")}`);
  if (rule.byMinute.join() !== "0") parts.push(`BYMINUTE=${rule.byMinute.join(",")}`);
  if (rule.bySetPos.length > 0) parts.push(`BYSETPOS=${rule.bySetPos.join(",")}`);
  if (rule.wkst !== 1) parts.push(`WKST=${DAY_NAMES[rule.wkst]}`);
  if (rule.count !== null) parts.push(`COUNT=${rule.count}`);
  if (rule.until !== null)
    parts.push(`UNTIL=${rule.until.toISOString().replace(/[-:]/g, "").slice(0, 15)}Z`);
  return parts.join(";");
}

/** Days of `month` (a Luxon month start) that BYDAY / BYMONTHDAY select. */
function daysInMonth(rule: Rule, month: DateTime, anchor: DateTime, defaultDay: boolean): number[] {
  const len = month.daysInMonth as number;
  let days: number[] | null = null;
  if (rule.byMonthDay.length > 0)
    days = rule.byMonthDay.map((d) => (d > 0 ? d : len + d + 1)).filter((d) => d >= 1 && d <= len);
  if (rule.byDay.length > 0) {
    const set = new Set<number>();
    for (const { weekday, n } of rule.byDay) {
      const firstOffset = (weekday - (month.weekday as number) + 7) % 7;
      const all: number[] = [];
      for (let d = 1 + firstOffset; d <= len; d += 7) all.push(d);
      if (n === 0) for (const d of all) set.add(d);
      else {
        const pick = n > 0 ? all[n - 1] : all[all.length + n];
        if (pick !== undefined) set.add(pick);
      }
    }
    days = days ? days.filter((d) => set.has(d)) : [...set];
  }
  if (days === null) days = defaultDay && anchor.day <= len ? [anchor.day] : [];
  return [...new Set(days)].sort((a, b) => a - b);
}

/** The local dates (y, m, d) of one period of the rule, in order. */
function datesOfPeriod(rule: Rule, start: DateTime, anchor: DateTime): DateTime[] {
  const monthOk = (d: DateTime) => rule.byMonth.length === 0 || rule.byMonth.includes(d.month);
  if (rule.freq === "DAILY") {
    const ok =
      monthOk(start) &&
      (rule.byMonthDay.length === 0 ||
        daysInMonth({ ...rule, byDay: [] }, start.startOf("month"), anchor, false).includes(
          start.day,
        )) &&
      (rule.byDay.length === 0 || rule.byDay.some((d) => d.weekday === start.weekday));
    return ok ? [start] : [];
  }
  if (rule.freq === "WEEKLY") {
    const weekdays =
      rule.byDay.length > 0 ? rule.byDay.map((d) => d.weekday) : [anchor.weekday as Weekday];
    const out: DateTime[] = [];
    for (let i = 0; i < 7; i++) {
      const d = start.plus({ days: i });
      if (weekdays.includes(d.weekday as Weekday) && monthOk(d)) out.push(d);
    }
    return out;
  }
  if (rule.freq === "MONTHLY") {
    if (!monthOk(start)) return [];
    return daysInMonth(rule, start, anchor, true).map((d) => start.set({ day: d }));
  }
  // YEARLY: the months of BYMONTH (or the anchor's), then days within each month.
  const months = rule.byMonth.length > 0 ? rule.byMonth : [anchor.month];
  const out: DateTime[] = [];
  const plain = rule.byMonthDay.length === 0 && rule.byDay.length === 0;
  for (const m of months) {
    const month = start.set({ month: m }).startOf("month");
    const days = plain
      ? anchor.day <= (month.daysInMonth as number)
        ? [anchor.day]
        : []
      : daysInMonth(rule, month, anchor, false);
    for (const d of days) out.push(month.set({ day: d }));
  }
  return out;
}

/** The first instant of the period containing `d` (local midnight). */
function periodStart(rule: Rule, d: DateTime): DateTime {
  const day = d.startOf("day");
  if (rule.freq === "DAILY") return day;
  if (rule.freq === "WEEKLY")
    return day.minus({ days: ((day.weekday as number) - rule.wkst + 7) % 7 });
  if (rule.freq === "MONTHLY") return day.startOf("month");
  return day.startOf("year");
}

function nextPeriod(rule: Rule, start: DateTime): DateTime {
  const n = rule.interval;
  if (rule.freq === "DAILY") return start.plus({ days: n }).startOf("day");
  if (rule.freq === "WEEKLY") return start.plus({ weeks: n }).startOf("day");
  if (rule.freq === "MONTHLY") return start.plus({ months: n }).startOf("month");
  return start.plus({ years: n }).startOf("year");
}

/** The occurrences of one period, as instants, after BYSETPOS. */
function occurrencesOfPeriod(rule: Rule, start: DateTime, anchor: DateTime): Date[] {
  const all: Date[] = [];
  for (const date of datesOfPeriod(rule, start, anchor))
    for (const hour of rule.byHour)
      for (const minute of rule.byMinute)
        all.push(
          DateTime.fromObject(
            { year: date.year, month: date.month, day: date.day, hour, minute },
            { zone: anchor.zone },
          ).toJSDate(),
        );
  all.sort((a, b) => a.getTime() - b.getTime());
  // Two wall times can be one instant (BYHOUR=2,3 on a spring-forward day): once only.
  const unique = all.filter((t, i) => i === 0 || t.getTime() !== all[i - 1]?.getTime());
  if (rule.bySetPos.length === 0) return unique;
  const picked = rule.bySetPos
    .map((p) => (p > 0 ? unique[p - 1] : unique[unique.length + p]))
    .filter((d): d is Date => d !== undefined);
  return [...new Map(picked.map((d) => [d.getTime(), d])).values()].sort(
    (a, b) => a.getTime() - b.getTime(),
  );
}

/**
 * Occurrences of `rule` in `zone`, from the series start `anchor` (inclusive) on, in order. Stops
 * at COUNT, UNTIL, or after `MAX_PERIODS` empty-or-not periods (a rule such as
 * `FREQ=YEARLY;BYMONTH=2;BYMONTHDAY=30` never occurs).
 */
export function* occurrences(rule: Rule, anchor: Date, zone: string): Generator<Date> {
  const a = DateTime.fromJSDate(anchor, { zone });
  if (!a.isValid) throw new Error(`invalid time zone ${zone}`);
  let start = periodStart(rule, a);
  let emitted = 0;
  for (let i = 0; i < MAX_PERIODS; i++) {
    for (const t of occurrencesOfPeriod(rule, start, a)) {
      if (t < anchor) continue;
      if (rule.until && t > rule.until) return;
      yield t;
      emitted += 1;
      if (rule.count !== null && emitted >= rule.count) return;
    }
    start = nextPeriod(rule, start);
  }
}

/** The first occurrence strictly after `after`, or null when the series has ended. */
export function nextAfter(rule: Rule, anchor: Date, zone: string, after: Date): Date | null {
  // Jump close to `after` when no COUNT has to be counted from the anchor: every period before
  // the one containing `after` is skipped on the INTERVAL grid.
  if (rule.count === null && after > anchor) {
    const a = DateTime.fromJSDate(anchor, { zone });
    const target = periodStart(rule, DateTime.fromJSDate(after, { zone }));
    let start = periodStart(rule, a);
    const unit =
      rule.freq === "DAILY"
        ? "days"
        : rule.freq === "WEEKLY"
          ? "weeks"
          : rule.freq === "MONTHLY"
            ? "months"
            : "years";
    const gap = Math.floor(target.diff(start, unit).get(unit) / rule.interval) - 1;
    if (gap > 0) start = start.plus({ [unit]: gap * rule.interval });
    for (let i = 0; i < MAX_PERIODS; i++) {
      for (const t of occurrencesOfPeriod(rule, start, a)) {
        if (t < anchor || t <= after) continue;
        if (rule.until && t > rule.until) return null;
        return t;
      }
      start = nextPeriod(rule, start);
    }
    return null;
  }
  for (const t of occurrences(rule, anchor, zone)) if (t > after) return t;
  return null;
}

/** The latest occurrence at or before `at` that is after `after`, or null (catch-up). */
export function latestBetween(
  rule: Rule,
  anchor: Date,
  zone: string,
  after: Date,
  at: Date,
): Date | null {
  let last: Date | null = null;
  let cur = nextAfter(rule, anchor, zone, after);
  // Bounded: a job that was down for years still finishes (minute rules are not supported).
  for (let i = 0; cur && cur <= at && i < 100_000; i++) {
    last = cur;
    cur = nextAfter(rule, anchor, zone, cur);
  }
  return last;
}
